/*
   `threadsQuery` — server-paginated + searchable + sortable + filtered page of
   QueueRows. Backend does the heavy lifting; the FE hook only maps the wire to
   UI rows. Filters arrive already built (`buildThreadsParams`), so the rows,
   the page count and the footer total all describe the same set.

   One request per page: assignee, engagement name and updated time come on
   the thread itself, rather than from re-fetching every review, the client
   list and a `/people` roster in the browser.
*/

import {
  keepPreviousData,
  queryOptions,
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query"

import { http } from "@/lib/http"
import { fromPageWire, toQueryParams, type Page, type PageWire } from "@/lib/pagination"
import { qk } from "@/lib/query-keys"
import type { TableQueryParams } from "@/lib/table"

import { toQueueRow, type QueueRow, type ThreadOutWire } from "../queue-data"
import type { QueueFilterParams } from "../queue-query-params"

export interface ThreadsQueryParams {
  page: number
  pageSize: number
  search: string
  sort: { field: string; dir: "asc" | "desc" } | null
  /** Part of the query key, so each filter combination is its own cache entry. */
  filters: QueueFilterParams
}

async function fetchThreadsPage(
  params: ThreadsQueryParams,
  signal: AbortSignal,
): Promise<Page<QueueRow>> {
  const wire = await http.get<PageWire<ThreadOutWire>>("/api/v1/threads", {
    params: toQueryParams(
      {
        page: params.page,
        pageSize: params.pageSize,
        sort: params.sort,
        filters: {},
        search: params.search,
      } satisfies TableQueryParams,
      { ...params.filters },
    ),
    signal,
  })
  return fromPageWire(wire, toQueueRow)
}

export const threadsQuery = (params: ThreadsQueryParams) =>
  queryOptions({
    queryKey: qk.threads.list(params),
    queryFn: ({ signal }) => fetchThreadsPage(params, signal),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  })

export function useThreadsQuery(params: ThreadsQueryParams): UseQueryResult<Page<QueueRow>> {
  return useQuery(threadsQuery(params))
}
